const article = {
  slug: "why-isnt-my-house-selling",
  cluster: "seller",
  title: "Why Isn't My House Selling? 9 Reasons (and How to Diagnose Yours)",
  description:
    "If your house isn't selling, the cause is almost always price, presentation, or promotion. Use this symptom-based checklist to find which one — and exactly what to fix.",
  audience: "Sellers & agents",
  funnelStage: "TOFU",
  datePublished: "2026-06-25",
  dateModified: "2026-06-25",
  readingTime: "8 min read",
  tldr: "A house that isn't selling almost always has a problem in one of three areas: price, presentation (photos and description), or promotion (exposure). The fastest way to tell which is to look at the traffic. Lots of online views but few showings usually means the price or the lead photo is wrong. Showings but no offers usually means condition, price, or expectations. Few views at all usually means exposure or a weak first photo.",
  blocks: [
    {
      type: "p",
      spans: [
        "When a home sits on the market, it can feel like bad luck. It almost never is. A listing that isn't selling is sending a signal, and that signal is readable. Buyers vote first with clicks, then with showings, then with offers — and where they stop voting tells you what's wrong.",
      ],
    },
    {
      type: "p",
      spans: [
        "Below are the nine reasons homes don't sell, grouped into the three things you actually control: ",
        { text: "price", bold: true },
        ", ",
        { text: "presentation", bold: true },
        ", and ",
        { text: "promotion", bold: true },
        ". Start with the diagnostic table — it points you to the likely cause in about ten seconds.",
      ],
    },
    {
      type: "h2",
      id: "diagnose-by-symptom",
      text: "Diagnose it by symptom",
    },
    {
      type: "table",
      headers: ["What you're seeing", "Most likely cause", "Where to look first"],
      rows: [
        [
          "Lots of online views, few or no showings",
          "Price or the lead photo",
          "The first photo and the list price vs. comps",
        ],
        [
          "Showings, but no offers",
          "Condition, price expectations, or layout",
          "Buyer feedback; staging and obvious repairs",
        ],
        [
          "Few online views at all",
          "Exposure or a weak first photo",
          "Lead photo, headline, and where it's syndicated",
        ],
        [
          "Strong start, then it went quiet",
          "It's gone stale / priced for a different market",
          "Days on market and recent price reductions nearby",
        ],
      ],
    },
    {
      type: "h2",
      id: "price-reasons",
      text: "Price problems (the most common by far)",
    },
    {
      type: "p",
      spans: [
        "Pricing is the number-one reason homes don't sell. The hard part is that an overpriced home doesn't look overpriced to the seller — it looks correct, because the seller is anchored to what they want or what they paid. Buyers, who shop dozens of listings side by side, see the mismatch immediately and simply skip it.",
      ],
    },
    {
      type: "ul",
      items: [
        [
          "The list price is above what comparable homes actually ",
          { text: "sold", italic: true },
          " for in the last 3–6 months (not what they're listed for).",
        ],
        "The home is priced just over a round-number search bucket (e.g., $515,000 hides from buyers capping searches at $500,000).",
        "The market shifted after you listed, and the price didn't move with it.",
        "Small, slow price cuts that always stay a step behind the market, instead of one decisive correction.",
      ],
    },
    {
      type: "callout",
      spans: [
        "Rule of thumb: if a home gets plenty of online views but very few showings, the market is telling you the price is too high. Views measure interest; showings measure whether the price clears the bar.",
      ],
    },
    {
      type: "h2",
      id: "presentation-reasons",
      text: "Presentation problems (photos, description, condition)",
    },
    {
      type: "p",
      spans: [
        "Most buyers meet your home as a thumbnail. If the first photo doesn't earn a click, nothing else in the listing gets a chance. After the click, the next few photos and the description decide whether they book a showing.",
      ],
    },
    {
      type: "ul",
      items: [
        [
          "A weak ",
          { text: "lead photo", bold: true },
          " — dark, cluttered, shot on a gray day, or leading with a bathroom instead of the best room.",
        ],
        "Too few photos, or photos in a confusing order that doesn't tell the story of the home.",
        [
          "A ",
          { text: "listing description", bold: true },
          " that lists facts the photos already show instead of selling the lifestyle and the standout features.",
        ],
        "Visible clutter, deferred maintenance, or bold personalization that makes buyers focus on the work instead of the home.",
      ],
    },
    {
      type: "p",
      spans: [
        "Two of these are fixable in an afternoon: re-order and improve the photos, and rewrite the description to lead with what's special. ",
        {
          text: "How to write a listing description that sells",
          href: "/resources/how-to-write-a-listing-description-that-sells",
        },
        " and ",
        {
          text: "the best order for listing photos",
          href: "/resources/best-order-for-listing-photos",
        },
        " walk through both.",
      ],
    },
    {
      type: "h2",
      id: "promotion-reasons",
      text: "Promotion problems (nobody's seeing it)",
    },
    {
      type: "ul",
      items: [
        "The listing isn't syndicated everywhere buyers look, or it launched quietly with no coming-soon push.",
        "It went live mid-week or during a holiday and missed the weekend traffic spike when most buyers shop.",
        "No agent open house, no social push, and no email to local buyer agents.",
      ],
    },
    {
      type: "h2",
      id: "stale-listing",
      text: "When a listing goes stale",
    },
    {
      type: "p",
      spans: [
        "Most homes that sell do so in the first few weeks. The longer a listing sits, the more buyers assume something is wrong with it — even if the only thing wrong was the original price. A high days-on-market count becomes its own problem. At that point the fix usually isn't another small price cut; it's a reset: new photos, a rewritten description, and a price that's clearly in the market, relaunched together. See ",
        {
          text: "how to relist a house that didn't sell",
          href: "/resources/how-to-relist-a-house",
        },
        ".",
      ],
    },
    {
      type: "h2",
      id: "how-to-fix",
      text: "How to find your specific problem",
    },
    {
      type: "p",
      spans: [
        "You don't have to guess. Pull your listing's view-to-showing ratio, compare your price to recent sold comps (not active listings), and look hard at your first three photos and your opening line. That's exactly what a ",
        { text: "Listino review", href: "/get-started" },
        " does in one pass: it assesses your price position, rewrites the description, re-sequences and touches up your photos, and includes a comparative market analysis — so you know which of the nine reasons is actually yours.",
      ],
    },
  ],
  faqs: [
    {
      question: "How long is too long for a house to sit on the market?",
      answer: [
        {
          type: "p",
          spans: [
            "It depends on your local market, but once a listing passes the typical time-to-offer for your area (often a few weeks in a balanced market), buyers start to wonder what's wrong. Track your days on market against recent local sales rather than a fixed number — and if you're well past it, treat it as a signal to reset price and presentation, not to wait longer.",
          ],
        },
      ],
    },
    {
      question: "Should I lower the price or wait?",
      answer: [
        {
          type: "p",
          spans: [
            "If your home is getting online views but few showings, waiting rarely helps — that pattern points to price. A single, decisive cut that puts you clearly within the range of recent sold comps tends to work better than a series of small reductions that keep trailing the market.",
          ],
        },
      ],
    },
    {
      question: "Can better photos really make a house sell?",
      answer: [
        {
          type: "p",
          spans: [
            "Photos won't fix an overpriced home, but they're often the difference between getting clicks and being skipped. Because most buyers decide whether to look based on the lead photo and the first few images, improving and re-sequencing them is one of the cheapest, fastest levers you have.",
          ],
        },
      ],
    },
  ],
  related: [
    "is-my-house-overpriced",
    "best-order-for-listing-photos",
    "how-to-write-a-listing-description-that-sells",
    "how-to-relist-a-house",
  ],
};

export default article;
